import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type Promotion = {
  _id: string;
  code: string;
  title: string;
  description?: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  scope: 'order' | 'category' | 'product';
  minOrderAmount: number;
  maxDiscountAmount?: number;
  isActive: boolean;
};

const defaultForm = {
  code: '',
  title: '',
  description: '',
  discountType: 'percent',
  discountValue: '0',
  scope: 'order',
  minOrderAmount: '0',
  maxDiscountAmount: '',
  isActive: true,
};

const Promotions = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<Promotion[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);
  const [form, setForm] = useState(defaultForm);

  const fetchRows = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/promotions', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 100 },
      });
      const list = Array.isArray(response.data?.promotions)
        ? response.data.promotions
        : Array.isArray(response.data)
          ? response.data
          : [];
      setRows(list);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load promotions.');
      setRows([]);
    }
  }, [token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setError('');
    setSuccess('');
    setShowDialog(true);
  };

  const openEdit = (row: Promotion) => {
    setEditing(row);
    setForm({
      code: row.code,
      title: row.title,
      description: row.description || '',
      discountType: row.discountType,
      discountValue: String(row.discountValue),
      scope: row.scope,
      minOrderAmount: String(row.minOrderAmount ?? 0),
      maxDiscountAmount: row.maxDiscountAmount !== undefined ? String(row.maxDiscountAmount) : '',
      isActive: row.isActive,
    });
    setError('');
    setSuccess('');
    setShowDialog(true);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

    const payload: any = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      discountType: form.discountType,
      discountValue: Number(form.discountValue || 0),
      scope: form.scope,
      minOrderAmount: Number(form.minOrderAmount || 0),
      maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : undefined,
      isActive: form.isActive,
    };

    try {
      if (editing?._id) {
        await axios.put(`/promotions/${editing._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Promotion updated successfully.');
      } else {
        await axios.post('/promotions', {
          ...payload,
          code: form.code.trim(),
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Promotion created successfully.');
      }

      setShowDialog(false);
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save promotion.');
    }
  };

  const handleDelete = async (row: Promotion) => {
    if (!token) return;
    if (!window.confirm(`Delete promotion ${row.code}?`)) return;

    try {
      await axios.delete(`/promotions/${row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Promotion deleted successfully.');
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete promotion.');
    }
  };

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Promotions</h4>
          <Button variant="dark" onClick={openCreate}>Add Promotion</Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Code</th>
              <th>Title</th>
              <th>Type</th>
              <th>Scope</th>
              <th>Value</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">No promotions found.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row._id}>
                <td>{row.code}</td>
                <td>{row.title}</td>
                <td>{row.discountType}</td>
                <td>{row.scope}</td>
                <td>{row.discountType === 'percent' ? `${row.discountValue}%` : `Rs ${Number(row.discountValue).toFixed(2)}`}</td>
                <td>{row.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <Button size="sm" variant="warning" className="me-2" onClick={() => openEdit(row)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(row)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Modal show={showDialog} onHide={() => setShowDialog(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Promotion' : 'Add Promotion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            {!editing ? (
              <Form.Group className="mb-2">
                <Form.Label>Code</Form.Label>
                <Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
              </Form.Group>
            ) : null}

            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Discount Type</Form.Label>
                  <Form.Select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value as 'percent' | 'fixed' })}>
                    <option value="percent">Percent</option>
                    <option value="fixed">Fixed</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Discount Value</Form.Label>
                  <Form.Control type="number" min="0" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} required />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Scope</Form.Label>
                  <Form.Select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value as 'order' | 'category' | 'product' })}>
                    <option value="order">Order</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Min Order Amount</Form.Label>
                  <Form.Control type="number" min="0" value={form.minOrderAmount} onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value })} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-2">
              <Form.Label>Max Discount Amount (optional)</Form.Label>
              <Form.Control type="number" min="0" value={form.maxDiscountAmount} onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" variant="dark">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Promotions;
